// contexts/UsageLimitContext.tsx
import React, { createContext, useContext, useState, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { ArrowLeftIcon, ArrowUpRightIcon } from "lucide-react";
import { Progress } from "@/components/ui/progress";

// utils/api.ts - Add this to your API utilities
export const handleApiError = (
  error: any,
  showLimitAlert: (data: any) => void
) => {
  if (error.status === 429 && error.data?.error?.includes("limit exceeded")) {
    showLimitAlert({
      feature: error.data.error.match(/'([^']+)'/)?.[1] || "feature",
      current: error.data.current,
      limit: error.data.limit,
    });
    return true; // Handled
  }
  return false; // Not handled
};

interface UsageLimitData {
  feature: string;
  current: number;
  limit: number;
  close_to_full?: boolean;
  text?: string;
}

interface UsageLimitContextType {
  showLimitAlert: (data: UsageLimitData) => void;
}

const UsageLimitContext = createContext<UsageLimitContextType | undefined>(
  undefined
);

export const useUsageLimit = () => {
  const context = useContext(UsageLimitContext);
  if (!context) {
    throw new Error("useUsageLimit must be used within a UsageLimitProvider");
  }
  return context;
};

interface UsageLimitProviderProps {
  children: ReactNode;
  onUpgrade?: () => void;
}

// Internal provider that uses useNavigate
const UsageLimitProviderInternal: React.FC<UsageLimitProviderProps> = ({
  children,
  onUpgrade,
}) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [limitData, setLimitData] = useState<UsageLimitData | null>(null);

  const showLimitAlert = (data: UsageLimitData) => {
    setLimitData(data);
    setIsOpen(true);
  };

  const handleUpgrade = () => {
    setIsOpen(false);
    if (onUpgrade) {
      onUpgrade();
    } else {
      window.open("https://calendly.com/k-ketselidis/discovery-call", "_blank", "noopener,noreferrer");
    }
  };

  const getTitle = () => {
    return limitData?.close_to_full
      ? "Close to Usage Limit"
      : "Usage Limit Reached";
  };

  const getDescription = () => {
    if (limitData?.text) {
      return limitData.text;
    }

    const baseMessage = limitData?.close_to_full
      ? "You are about to reach your maximum usage for"
      : "You've reached your limit for";

    return (
      <>
        {baseMessage} <span className="underline underline-offset-2">{limitData?.feature}</span>.
        <br />
        <br />
        Current usage: 
        <span className="font-mono mx-2">
          {limitData?.current}/{limitData?.limit}
        </span>
        <br />
        <Progress value={100} className="h-1 mt-1 mb-2"/>
        <p className="text-xs text-muted-foreground">
          Upgrade your plan to continue using this feature.
        </p>
      </>
    );
  };

  return (
    <UsageLimitContext.Provider value={{ showLimitAlert }}>
      {children}
      {isOpen && <AlertDialog open={true} defaultOpen={true}>
        <AlertDialogContent className="max-w-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>{getTitle()}</AlertDialogTitle>
            <AlertDialogDescription>{getDescription()}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button size="sm" variant="ghost" onClick={() => {setIsOpen(false); navigate("/")}}>
              <ArrowLeftIcon/>
              Go home
            </Button>
            <Button size="sm" onClick={handleUpgrade}>
              Upgrade Plan
              <ArrowUpRightIcon/>
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>}
    </UsageLimitContext.Provider>
  );
};

// External provider that can be used outside router
export const UsageLimitProvider: React.FC<UsageLimitProviderProps> = ({
  children,
  onUpgrade,
}) => {
  return (
    <UsageLimitProviderInternal onUpgrade={onUpgrade}>
      {children}
    </UsageLimitProviderInternal>
  );
};
